/**
 * Unified business inbox links — header strip (L3-NOTIF-v2 · UA-V2-3)
 *
 * Points to /approvals/pending and /flow/dispatch with live counts.
 * Complements NotificationPanel (neuro realtime) — no websocket required.
 *
 * @see docs/inflomatrix-constitution/ui-architecture/07-CROSS-CUTTING-PATTERNS.md §4
 */

import React from 'react';
import { Badge, Button, Space, Tooltip } from 'antd';
import { InboxOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useMenuVisibility } from '../../hooks/ui/useMenuVisibility';
import { useBusinessInboxCounts } from '@frontend/domain-hooks/shared/useBusinessInboxCounts';

export function BusinessInboxStrip(): React.ReactElement | null {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { canAccessPath } = useMenuVisibility();
  const tenantId = currentTenant?.id ?? 'default_tenant';

  const canApprovals = canAccessPath('/approvals/pending');
  const canDispatch = canAccessPath('/flow/dispatch');
  const fetchEnabled = canApprovals || canDispatch;

  const { counts } = useBusinessInboxCounts(tenantId, { enabled: fetchEnabled });

  if (!fetchEnabled) {
    return null;
  }

  const showApprovals = canApprovals && counts.approvalPending > 0;
  const showDispatch = canDispatch && counts.dispatch > 0;

  if (!showApprovals && !showDispatch) {
    return null;
  }

  return (
    <Space size="small" data-testid="business-inbox-strip" aria-label="업무 인박스">
      {showApprovals ? (
        <Tooltip title="승인·검토 대기 (H-04)">
          <Badge count={counts.approvalPending} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              size="small"
              icon={<InboxOutlined />}
              onClick={() => navigate('/approvals/pending')}
              aria-label={`승인 대기 ${counts.approvalPending}건`}
            >
              승인
            </Button>
          </Badge>
        </Tooltip>
      ) : null}
      {showDispatch ? (
        <Tooltip title="출고·배송 대기 (H-08)">
          <Badge count={counts.dispatch} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              size="small"
              icon={<SendOutlined />}
              onClick={() => navigate('/flow/dispatch')}
              aria-label={`출고 대기 ${counts.dispatch}건`}
            >
              출고
            </Button>
          </Badge>
        </Tooltip>
      ) : null}
    </Space>
  );
}

export default BusinessInboxStrip;
